import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import PropertyService from '../../services/PropertyService';
import BookingService from '../../services/BookingService';
import {
  PROPERTY_STATUSES, getPropertyStatus, getPropertyCategory,
  getBookingStatus, fmtPrice, fmtSize, parseJson, getInventorySource,
} from '../../constants/propertyConstants';

const card = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.07)', padding: 24 };
const label = { fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', marginBottom: 4 };
const value = { fontSize: 14, fontWeight: 600, color: '#1a1f36', margin: 0 };

const InfoRow = ({ lbl, val }) => (
  <div style={{ marginBottom: 16 }}>
    <p style={label}>{lbl}</p>
    <p style={value}>{val || '—'}</p>
  </div>
);

export default function PropertyDetail() {
  const { id }  = useParams();
  const navigate = useNavigate();
  const toast    = useRef(null);

  const [prop,     setProp]     = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('info'); // 'info' | 'bookings' | 'docs'

  useEffect(() => {
    Promise.all([
      PropertyService.getPropertyById(id),
      BookingService.getByPropertyId(id),
    ]).then(([p, b]) => {
      setProp(p);
      setBookings(Array.isArray(b) ? b : b?.$values ?? []);
    }).catch(() => {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load property' });
    }).finally(() => setLoading(false));
  }, [id]);

  const changeStatus = async (status) => {
    try {
      await PropertyService.updatePropertyStatus(Number(id), status);
      setProp(prev => ({ ...prev, propertyStatus: status }));
      toast.current?.show({ severity: 'success', summary: 'Updated', life: 2000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Update failed' });
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '100px 0' }}>
      <i className="fas fa-spinner fa-spin" style={{ fontSize: 36, color: '#5e72e4' }} />
    </div>
  );

  if (!prop) return <div style={{ padding: 40, textAlign: 'center', color: '#8898aa' }}>Property not found.</div>;

  const st  = getPropertyStatus(prop.propertyStatus);
  const cat = getPropertyCategory(prop.propertyCategory);
  const invSrc = getInventorySource(prop.inventorySource || 'Own');
  const amenities = parseJson(prop.amenitiesJson, []);
  const docs      = parseJson(prop.documentsJson, []);
  const gallery   = parseJson(prop.imagesJson, []);

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* Breadcrumb */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#8898aa' }}>
        <button onClick={() => navigate('/propertylisting')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5e72e4', fontWeight: 600, padding: 0 }}>
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />Properties
        </button>
        <span>/</span>
        <span style={{ color: '#1a1f36', fontWeight: 600 }}>{prop.propertyName}</span>
      </div>

      {/* Hero */}
      <div style={{ ...card, background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,.07)' }} />
        <div style={{ position: 'absolute', right: 40, bottom: -40, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ background: 'rgba(255,255,255,.2)', padding: '3px 12px', borderRadius: 20, fontSize: 12 }}>
                <i className={cat.icon} style={{ marginRight: 5 }} />{cat.label}
              </span>
              <span style={{ background: st.bg, color: st.color, padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                <i className={st.icon} style={{ marginRight: 4 }} />{st.label}
              </span>
            </div>
            <h2 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 800 }}>{prop.propertyName}</h2>
            {prop.projectName && (
              <p style={{ margin: '0 0 4px', opacity: .85 }}>
                <i className="fas fa-city" style={{ marginRight: 6 }} />{prop.projectName}
                {prop.towerBlock && <span style={{ marginLeft: 8 }}>· Tower {prop.towerBlock}</span>}
                {prop.floorNumber && <span style={{ marginLeft: 8 }}>· Floor {prop.floorNumber}</span>}
                {prop.unitNumber && <span style={{ marginLeft: 8 }}>· Unit {prop.unitNumber}</span>}
              </p>
            )}
            <p style={{ margin: 0, opacity: .8 }}>
              <i className="fas fa-map-marker-alt" style={{ marginRight: 6 }} />
              {[prop.streetAddress, prop.city, prop.state, prop.country].filter(Boolean).join(', ')}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, opacity: .8, fontSize: 12 }}>Price</p>
            <h2 style={{ margin: '4px 0', fontSize: 30, fontWeight: 900 }}>{fmtPrice(prop.price)}</h2>
            <p style={{ margin: 0, opacity: .7, fontSize: 13 }}>{fmtSize(prop.size, prop.sizeUnit)}</p>
            {prop.maintenanceCharge > 0 && <p style={{ margin: '4px 0 0', opacity: .7, fontSize: 12 }}>Maint: {fmtPrice(prop.maintenanceCharge)}/mo</p>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button onClick={() => navigate(`/editproperty/${id}`)}
            style={{ padding: '9px 20px', background: 'rgba(255,255,255,.2)', color: '#fff', border: '1px solid rgba(255,255,255,.4)', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            <i className="fas fa-edit" style={{ marginRight: 6 }} />Edit
          </button>
          <button onClick={() => navigate(`/property/${id}/book`)}
            style={{ padding: '9px 20px', background: '#fff', color: '#5e72e4', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            <i className="fas fa-file-contract" style={{ marginRight: 6 }} />Create Booking
          </button>
        </div>
      </div>

      {/* Status changer */}
      <div style={{ ...card, marginBottom: 20 }}>
        <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#1a1f36', fontSize: 14 }}>
          <i className="fas fa-exchange-alt" style={{ color: '#5e72e4', marginRight: 8 }} />Change Status
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PROPERTY_STATUSES.map(s => (
            <button key={s.id} onClick={() => changeStatus(s.id)}
              style={{ padding: '8px 16px', borderRadius: 10, border: `2px solid ${s.color}`, background: s.id === prop.propertyStatus ? s.color : s.bg, color: s.id === prop.propertyStatus ? '#fff' : s.color, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              <i className={s.icon} style={{ marginRight: 5 }} />{s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#fff', borderRadius: 12, padding: 5, width: 'fit-content', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
        {[['info', 'fas fa-info-circle', 'Details'], ['bookings', 'fas fa-file-contract', `Bookings (${bookings.length})`], ['docs', 'fas fa-folder-open', 'Docs & Media']].map(([key, icon, lbl]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: tab === key ? '#5e72e4' : 'transparent', color: tab === key ? '#fff' : '#8898aa' }}>
            <i className={icon} style={{ marginRight: 7 }} />{lbl}
          </button>
        ))}
      </div>

      {/* ── Details Tab ── */}
      {tab === 'info' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="prop-grid">
          <style>{`@media(max-width:768px){.prop-grid{grid-template-columns:1fr!important}}`}</style>

          {/* General */}
          <div style={card}>
            <h6 style={{ margin: '0 0 18px', color: '#5e72e4', fontWeight: 700 }}><i className="fas fa-home" style={{ marginRight: 7 }} />General Info</h6>
            <InfoRow lbl="Owner / Builder" val={prop.ownerName || prop.builderName} />
            <InfoRow lbl="Inventory" val={invSrc.label} />
            {(prop.inventorySource || 'Own') === 'ChannelPartner' && (
              <>
                <InfoRow lbl="Linked partner" val={prop.builderCompanyName || (prop.builderId ? `Builder #${prop.builderId}` : '—')} />
                {prop.channelCommissionPercent != null && prop.channelCommissionPercent !== '' && (
                  <InfoRow lbl="Commission % (unit)" val={`${prop.channelCommissionPercent}%`} />
                )}
                {prop.channelAgreementNotes ? (
                  <InfoRow lbl="Agreement notes" val={prop.channelAgreementNotes} />
                ) : null}
              </>
            )}
            <InfoRow lbl="Property Type"  val={prop.propertyType} />
            <InfoRow lbl="Category"       val={cat.label} />
            <InfoRow lbl="Year Built"     val={prop.yearBuilt > 0 ? prop.yearBuilt : null} />
            <InfoRow lbl="Bedrooms"       val={prop.bedrooms > 0 ? prop.bedrooms : null} />
            <InfoRow lbl="Bathrooms"      val={prop.bathrooms > 0 ? prop.bathrooms : null} />
            <InfoRow lbl="Facing"         val={prop.facing} />
          </div>

          {/* Location */}
          <div style={card}>
            <h6 style={{ margin: '0 0 18px', color: '#11cdef', fontWeight: 700 }}><i className="fas fa-map-marker-alt" style={{ marginRight: 7 }} />Location</h6>
            <InfoRow lbl="Street"      val={prop.streetAddress} />
            <InfoRow lbl="City"        val={prop.city} />
            <InfoRow lbl="State"       val={prop.state} />
            <InfoRow lbl="Country"     val={prop.country} />
            <InfoRow lbl="Postal Code" val={prop.postalCode} />
            {prop.latitude && (
              <div style={{ marginTop: 8 }}>
                <a href={`https://maps.google.com/?q=${prop.latitude},${prop.longitude}`} target="_blank" rel="noreferrer"
                  style={{ color: '#5e72e4', fontSize: 13, fontWeight: 600 }}>
                  <i className="fas fa-map" style={{ marginRight: 5 }} />View on Google Maps
                </a>
              </div>
            )}
          </div>

          {/* Description */}
          {prop.description && (
            <div style={{ ...card, gridColumn: 'span 2' }}>
              <h6 style={{ margin: '0 0 12px', color: '#825ee4', fontWeight: 700 }}><i className="fas fa-align-left" style={{ marginRight: 7 }} />Description</h6>
              <p style={{ color: '#525f7f', lineHeight: 1.7, margin: 0 }}>{prop.description}</p>
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <div style={{ ...card, gridColumn: 'span 2' }}>
              <h6 style={{ margin: '0 0 14px', color: '#2dce89', fontWeight: 700 }}><i className="fas fa-star" style={{ marginRight: 7 }} />Amenities</h6>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {amenities.map(a => (
                  <span key={a} style={{ background: '#2dce8915', color: '#2dce89', border: '1px solid #2dce8944', padding: '5px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                    <i className="fas fa-check" style={{ marginRight: 5 }} />{a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Bookings Tab ── */}
      {tab === 'bookings' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h6 style={{ margin: 0, fontWeight: 700, color: '#1a1f36' }}><i className="fas fa-file-contract" style={{ color: '#5e72e4', marginRight: 8 }} />Booking History</h6>
            <button onClick={() => navigate(`/property/${id}/book`)}
              style={{ padding: '8px 16px', background: '#5e72e4', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              <i className="fas fa-plus" style={{ marginRight: 6 }} />New Booking
            </button>
          </div>
          {bookings.length === 0 ? (
            <p style={{ color: '#adb5bd', textAlign: 'center', padding: '32px 0' }}>No bookings yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f9fe' }}>
                    {['Buyer', 'Phone', 'Status', 'Booking Amt', 'Total', 'Date', 'Broker'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#8898aa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => {
                    const bs = getBookingStatus(b.bookingStatus);
                    return (
                      <tr key={b.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                        <td style={{ padding: '11px 14px', fontWeight: 600 }}>{b.buyerName}</td>
                        <td style={{ padding: '11px 14px', color: '#8898aa' }}>{b.buyerPhone}</td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{ background: `${bs.color}20`, color: bs.color, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{bs.label}</span>
                        </td>
                        <td style={{ padding: '11px 14px', fontWeight: 700, color: '#5e72e4' }}>{fmtPrice(b.bookingAmount)}</td>
                        <td style={{ padding: '11px 14px', fontWeight: 700, color: '#1a1f36' }}>{fmtPrice(b.totalAmount)}</td>
                        <td style={{ padding: '11px 14px', color: '#8898aa' }}>{b.bookingDate ? new Date(b.bookingDate).toLocaleDateString('en-IN') : '—'}</td>
                        <td style={{ padding: '11px 14px', color: '#8898aa' }}>{b.brokerName || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Docs Tab ── */}
      {tab === 'docs' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <h6 style={{ margin: 0, fontWeight: 700, color: '#1a1f36' }}>
              <i className="fas fa-folder-open" style={{ color: '#fb6340', marginRight: 8 }} />Docs &amp; Media
            </h6>
            <button
              type="button"
              onClick={() => navigate(`/editproperty/${id}`)}
              style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              <i className="fas fa-upload" style={{ marginRight: 8 }} />Upload / manage files
            </button>
          </div>

          {gallery.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', margin: '0 0 12px' }}>Photos</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12 }}>
                {gallery.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noreferrer" style={{ display: 'block', borderRadius: 12, overflow: 'hidden', border: '1px solid #e3e6ec' }}>
                    <img src={src} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                  </a>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: 12, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', margin: '0 0 12px' }}>Documents</p>
          {docs.length === 0 ? (
            <p style={{ color: '#adb5bd', fontSize: 14, margin: gallery.length ? '0 0 8px' : 0 }}>
              {gallery.length ? 'No PDF/other documents linked yet.' : ''}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
              {docs.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', border: '1px solid #e3e6ec', borderRadius: 10, textDecoration: 'none', color: '#1a1f36' }}>
                  <i className="fas fa-file-pdf" style={{ color: '#f5365c', fontSize: 22, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || `Document ${i + 1}`}</span>
                </a>
              ))}
            </div>
          )}

          {docs.length === 0 && gallery.length === 0 && (
            <div style={{ textAlign: 'center', padding: '28px 16px', background: '#f8f9fe', borderRadius: 12, marginTop: 8 }}>
              <i className="fas fa-file-upload" style={{ fontSize: 36, color: '#c5cde0', marginBottom: 10 }} />
              <p style={{ color: '#8898aa', margin: '0 0 14px' }}>No documents or photos yet.</p>
              <button
                type="button"
                onClick={() => navigate(`/editproperty/${id}`)}
                style={{ padding: '10px 22px', background: '#5e72e4', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>
                Add docs &amp; media
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
